import React, { useMemo } from "react";
import _ from "lodash";

import { IProductStatus } from "src/core/products/api/products.api";
import CurrentProductStatus from "src/core/products/components/ProductStatuses/CurrentProductStatus";
import FutureProductStatuses, { ProductStatusRowType } from "src/core/products/components/ProductStatuses/FutureProductStatuses";

interface ProductStatusesProps {
  type: "current" | "future";
  currentStatus: IProductStatus;
  productStatuses?: IProductStatus[];
  futureStatusesRef?: React.ForwardedRef<{currentFutureStatuses: ProductStatusRowType[]}>
  displayOnly?: boolean;
}

// filters current status and any start date that is older than today's date
const transformProductStatuses = (productStatuses: IProductStatus[], currentStatusId: number) => {
  const today = new Date();
  const filteredProductStatuses = _.filter(productStatuses, productStatus => productStatus.id !== currentStatusId);

  return filteredProductStatuses.reduce<IProductStatus[] | []>((accumulated: IProductStatus[], currentProductStatus: IProductStatus) => {
    const result = [ ...accumulated ];

    const transformedProductStatus = {
      ...currentProductStatus,
      startDate: new Date(currentProductStatus.startDate),
      endDate: currentProductStatus.endDate ? new Date(currentProductStatus.endDate) : currentProductStatus.endDate
    };

    if (transformedProductStatus.startDate < today) {
      return result;
    }

    result.push(transformedProductStatus);

    return result;
  }, []);
};

/**
 * Based on type, returns current status row or list of future statuses. Row consists of status, start & end date.
 */
const ProductStatuses: React.ReactFCWithChildren<ProductStatusesProps> = ({
  type,
  currentStatus,
  productStatuses = [],
  futureStatusesRef,
  displayOnly
}) => {
  const transformedStatuses = useMemo(() => {
    return transformProductStatuses(productStatuses, currentStatus.id as number);
  }, [ productStatuses ]);

  if (type === "current") {
    return <CurrentProductStatus currentStatus={currentStatus} />;
  }

  return (
    <FutureProductStatuses
      productStatuses={transformedStatuses}
      productId={currentStatus.productId}
      ref={futureStatusesRef}
      displayOnly={displayOnly}
    />
  );
};

export default ProductStatuses;
